/**
 * Hook to monitor session state and show expiration modal.
 *
 * Three layers of protection:
 *   1. Dynamic setTimeout — schedules a check ~1 min before token expiry.
 *   2. Fallback setInterval — every 5 min in case the setTimeout is lost.
 *   3. visibilitychange listener — fires immediately when user returns to a
 *      backgrounded tab (browsers throttle timers in hidden tabs, so the
 *      setTimeout/setInterval may be significantly delayed).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { useNavigate } from 'react-router-dom';
import { sessionManager } from '../services/sessionManager';
import { tokenRefresher } from '../services/tokenRefresher';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('SESSION_MONITOR');

export const useSessionMonitor = () => {
  const [isSessionExpiredModalOpen, setIsSessionExpiredModalOpen] = useState(false);
  const [sessionContext, setSessionContext] = useState<any>(null);
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle session expired event
  useEffect(() => {
    const unsubscribe = sessionManager.onSessionExpired((context) => {
      log.warn('Session expired event received', context);
      setSessionContext(context);
      setIsSessionExpiredModalOpen(true);
    });

    return unsubscribe;
  }, []);

  // Monitor token expiration proactively
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTokenExpiry = async () => {
      const authToken = localStorage.getItem('authToken');
      const idToken = localStorage.getItem('id_token');
      const token = authToken || idToken;

      if (!token) {
        log.warn('No token found during expiry check');
        return;
      }

      if (sessionManager.isTokenExpired(token)) {
        log.warn('Token expired — attempting silent refresh before signing out');
        const newToken = await tokenRefresher.tryRefresh();
        if (newToken) {
          log.log('Silent refresh succeeded — session preserved');
          scheduleNextCheck(newToken);
          return;
        }
        log.warn('Silent refresh failed — triggering session expired');
        sessionManager.handleSessionExpired();
      } else {
        scheduleNextCheck(token);
      }
    };

    const scheduleNextCheck = (token: string) => {
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
      const timeUntilExpiry = sessionManager.getTimeUntilExpiry(token);
      if (timeUntilExpiry) {
        const checkIn = Math.max(Math.min(timeUntilExpiry - 60_000, 60_000), 5_000);
        expiryTimerRef.current = setTimeout(checkTokenExpiry, checkIn);
      }
    };

    // When the tab becomes visible again, check immediately — timers may have
    // been throttled while the tab was in the background.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        log.log('Tab became visible — checking token expiry');
        checkTokenExpiry();
      }
    };

    checkTokenExpiry();
    const interval = setInterval(checkTokenExpiry, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  // Handle login action from modal
  const handleLogin = useCallback(() => {
    log.log('User clicked login from session expired modal');
    
    // Clear auth state but preserve redirect
    const redirectPath = localStorage.getItem('redirectAfterLogin');
    logout();
    
    // Close modal
    setIsSessionExpiredModalOpen(false);
    sessionManager.reset();
    
    // Navigate to home (which will show login)
    navigate('/', { replace: true });
    
    // Restore redirect path if it was cleared
    if (redirectPath) {
      localStorage.setItem('redirectAfterLogin', redirectPath);
    }
  }, [logout, navigate]);

  return {
    isSessionExpiredModalOpen,
    sessionContext,
    handleLogin
  };
};


